import Head from "next/head";
import Link from "next/link";
import { useState, useEffect } from "react";
import { useCart } from "@context/CartContext";

export default function ProgramsIndex() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const response = await fetch("/api/programs");
      const result = await response.json();

      if (result.success) {
        setPrograms(result.data);
      } else {
        setError("Failed to load programs");
      }
    } catch (err) {
      console.error("Error fetching programs:", err);
      setError("Failed to load programs. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const getProgramImage = (slug) => {
    const images = {
      "3-day-immersive": "/program/p-1.jpg",
      "4-saturday-flexible": "/program/p-2.jpg",
      "odc-workshops": "/program/p-3.jpg",
      "full-leadership": "/program/p-1.jpg",
      corporate: "/program/p-1.jpg",
    };
    return images[slug] || "/program/p-1.jpg";
  };

  const getDisplayPrograms = () => {
    return programs.flatMap((program) => {
      if (program.slug !== "full-leadership") {
        if (program.slug === "odc-workshops") {
          return [
            {
              ...program,
              badge: "Online",
              description:
                "High-impact, shorter-format soft skills workshops for teams and professionals who need focused development without a full multi-week commitment.",
            },
          ];
        }
        return [program];
      }

      const immersiveFormat = program.formats?.find(
        (format) => format.name === "3-Day Immersive",
      );
      const flexibleFormat = program.formats?.find(
        (format) => format.name === "4-Saturday Flexible",
      );

      return [
        {
          ...program,
          _id: `${program._id}-immersive`,
          title: "3-Day Immersive Leadership Program",
          description:
            "Intensive 18-hour on-site leadership experience built for deep transformation, live application, and peer networking.",
          slug: "3-day-immersive",
          formats: immersiveFormat ? [immersiveFormat] : [],
          badge: "On-Site",
        },
        {
          ...program,
          _id: `${program._id}-flexible`,
          title: "4-Saturday Flexible Leadership Program",
          description:
            "A 12-hour live online pathway spread across four Saturdays for professionals who need structure with flexibility.",
          slug: "4-saturday-flexible",
          formats: flexibleFormat ? [flexibleFormat] : [],
          badge: "Most Popular",
        },
      ];
    });
  };

  const getLowestPrice = (program) => {
    if (program.isContactForPrice || program.isLeadOnly) {
      return "Price Upon Request";
    }
    if (!program.formats || program.formats.length === 0) {
      return "Price Upon Request";
    }
    const prices = program.formats.map((f) => f.price).filter((p) => p >= 0);
    if (prices.length === 0) {
      return "Price Upon Request";
    }
    const lowestPrice = Math.min(...prices);
    if (lowestPrice === 0) return "Price Upon Request";
    return `Starting at ₹${lowestPrice.toLocaleString()}`;
  };

  const handleAddToCart = (e, program, format) => {
    e.preventDefault();
    addToCart({
      programId: program._id,
      formatId: format.id,
      title: program.title,
      price: format.price,
      featuredImg: program.featuredImg,
    });
  };

  const displayPrograms = getDisplayPrograms();

  return (
    <>
      <Head>
        <title>Programs | L.I.S.T Learning Pathways</title>
        <meta
          name="description"
          content="Explore our transformative leadership programs designed for to modern professional."
        />
      </Head>

      <main className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-brand-dark pt-20 pb-16 lg:pt-28 lg:pb-20">
          <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-[400px] h-[400px] bg-brand-primary/10 rounded-full blur-3xl"></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10 text-center">
            <span className="inline-block px-4 py-1.5 bg-brand-primary/20 text-white text-base font-bold rounded-full mb-4">
              Education Hub
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6 leading-[1.1] tracking-tight">
              Leadership <span className="text-brand-primary">Pathways</span>
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Choose a program that aligns with your current professional goals
              and learning style. All our pathways are built on core L.I.S.T
              framework.
            </p>
          </div>
        </section>

        {/* Programs Grid */}
        <section className="py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : error ? (
              <div className="text-center py-20">
                <p className="text-red-500 mb-4">{error}</p>
                <button
                  onClick={fetchPrograms}
                  className="px-6 py-3 bg-brand-primary text-white font-bold rounded-md hover:bg-brand-dark transition-all"
                >
                  Try Again
                </button>
              </div>
            ) : displayPrograms.length === 0 ? (
              <div className="text-center py-20 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-bold text-brand-dark mb-2">
                  No Programs Available
                </h3>
                <p className="text-gray-600 mb-6">
                  Check back soon for new programs or contact us for custom
                  training solutions.
                </p>
                <Link
                  href="/contact"
                  className="inline-block px-6 py-3 bg-brand-primary text-white font-bold rounded-md hover:bg-brand-dark transition-all"
                >
                  Contact Us
                </Link>
              </div>
            ) : (
              <div className="grid lg:grid-cols-3 gap-6">
                {displayPrograms.map((program) => (
                  <div
                    key={program._id}
                    className="group flex flex-col bg-white border border-gray-100 rounded-lg overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="h-72 relative overflow-hidden bg-gray-50 flex items-center justify-center">
                      <img
                        src={
                          program.featuredImg || getProgramImage(program.slug)
                        }
                        alt={program.title}
                        className="w-full h-full object-contain object-center grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
                      />
                      <div className="absolute inset-0 bg-transparent group-hover:bg-brand-dark/10 transition-all duration-300"></div>
                      {program.badge && (
                        <div className="absolute top-4 left-4">
                          <span className="bg-white/90 backdrop-blur-sm text-brand-dark text-base font-bold px-3 py-1 rounded-full shadow">
                            {program.badge}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-6 flex flex-col flex-grow">
                      <h2 className="text-xl font-extrabold mb-3 text-brand-dark group-hover:text-brand-primary transition-colors tracking-tight">
                        {program.title}
                      </h2>
                      <p className="text-gray-500 text-base leading-relaxed mb-4 flex-grow">
                        {program.description}
                      </p>

                      {/* Pricing Display */}
                      <div className="mb-6">
                        {program.isContactForPrice || program.isLeadOnly ? (
                          <div className="bg-brand-primary/5 border border-brand-primary/20 rounded-lg p-3">
                            <span className="text-sm text-gray-500 block mb-1">
                              Pricing
                            </span>
                            <span className="text-base font-bold text-brand-primary">
                              Price Upon Request
                            </span>
                          </div>
                        ) : program.formats && program.formats.length > 0 ? (
                          <div className="space-y-2">
                            {program.formats
                              .slice(0, 2)
                              .map((format, index) => (
                                <div
                                  key={index}
                                >
                                  <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-600">
                                      {format.name}
                                    </span>
                                    <span className="font-bold text-brand-dark">
                                      {format.price > 0 ? `₹${format.price.toLocaleString()}` : "Price upon request"}
                                    </span>
                                  </div>
                                  <p className="text-[10px] text-gray-400 font-medium text-right mt-0.5">(Inclusive of 18% GST)</p>
                                </div>
                              ))}
                            {program.formats.length > 2 && (
                              <span className="text-xs text-brand-primary font-semibold">
                                +{program.formats.length - 2} more formats
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-500 text-sm">
                            Contact for details
                          </span>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col sm:flex-row gap-3 mt-auto">
                        <Link
                          href={`/programs/${program.slug}`}
                          className="flex-1 text-center px-4 py-3 border-2 border-brand-primary/20 text-brand-dark font-bold rounded-lg hover:bg-gray-50 transition-all text-sm"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}
